import { translate } from "@/config/translator";
import {
  faArrowUpFromBracket,
  faFileLines,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFileUploadArea } from "./fileUploadArea/useFileUploadArea";
import { clsx } from "clsx/lite";

type FileUploadAreaProps =
  | {
      onChange: (files: File[]) => void;
      singleFile?: false;
    }
  | {
      onChange: (files: File) => void;
      singleFile: true;
    };

export function FileUploadArea({ onChange, singleFile }: FileUploadAreaProps) {
  const {
    lang,
    onDragOver,
    onDragLeave,
    onDrop,
    inputId,
    onFileSelect,
    isDragging,
  } = useFileUploadArea(
    singleFile
      ? {
          singleFile,
          onChange,
        }
      : {
          singleFile,
          onChange,
        },
  );

  return (
    <div>
      <input
        type="file"
        id={inputId}
        className="hidden"
        onChange={onFileSelect}
        multiple={!singleFile}
      />
      <label htmlFor={inputId}>
        <div
          className={clsx(
            "flex",
            "flex-row",
            isDragging ? "gap-5" : "gap-3",
            "w-full",
            "justify-center",
            "h-14",
            "items-center",
            "cursor-pointer",
            "group",
            "rounded-lg",
            "duration-300",
            "hover:bg-primary-50",
            "ease-out",
            "border-2",
            "border-dashed",
            "hover:border-dashed",
            "hover:border-primary",
            "outline-none",
            isDragging ? "border-primary" : "border-neutral",
          )}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <div
            className={clsx(
              "relative",
              "flex",
              "items-center",
              "justify-center",
              "h-10",
              "w-10",
              "pointer-events-none",
            )}
          >
            {/* Center */}
            <FontAwesomeIcon
              icon={isDragging ? faFileLines : faArrowUpFromBracket}
              className={clsx(
                "absolute",
                "text-2xl",
                "text-font-secondary",
                "group-hover:text-primary",
                "transition-all",
                "duration-300",
                "ease-out",
                isDragging ? "scale-115" : "scale-100",
              )}
            />

            {/* Left */}
            <FontAwesomeIcon
              icon={faFileLines}
              className={clsx(
                "absolute",
                "text-2xl",
                "text-font-secondary",
                "group-hover:text-primary",
                "transition-all",
                "duration-300",
                "ease-out",
                isDragging
                  ? "-translate-x-5 translate-y-1 -rotate-16 opacity-80 delay-200"
                  : "translate-x-0 translate-y-0 rotate-0 opacity-0",
              )}
            />

            {/* Right */}
            <FontAwesomeIcon
              icon={faFileLines}
              className={clsx(
                "absolute",
                "text-2xl",
                "text-font-secondary",
                "group-hover:text-primary",
                "transition-all",
                "duration-300",
                "ease-out",
                isDragging
                  ? "translate-x-5 translate-y-1 rotate-16 opacity-80"
                  : "translate-x-0 translate-y-0 rotate-0 opacity-0",
              )}
            />
          </div>
          <p
            className={clsx(
              "text-font-secondary",
              "group-hover:text-primary",
              "lg:text-lg",
              "sm:text-sm",
              "pointer-events-none",
            )}
          >
            {isDragging
              ? singleFile
                ? translate("dropFileHere", lang)
                : translate("dropFilesHere", lang)
              : singleFile
                ? translate("dropOrSelectFile", lang)
                : translate("dropOrSelectFiles", lang)}
          </p>
        </div>
      </label>
    </div>
  );
}
